/**
 * The arithmetic under the canvas.
 *
 * Everything here is pure and framework-free on purpose. A canvas editor
 * goes wrong in geometry long before it goes wrong in React — a handle
 * that resizes the wrong way once the shape is rotated, a hit test that
 * misses by the stroke width, a marquee that only selects left-to-right
 * — and none of that is testable through a browser at the speed it needs
 * to be tested. So the browser layer stays thin and every decision that
 * can be made from numbers alone is made here.
 *
 * COORDINATES. Two spaces, and confusing them is the bug that shows up
 * as "it works until you zoom":
 *   · WORLD  — the diagram's own infinite plane. Shapes live here, and
 *              the numbers match Lucid's, which are large and signed.
 *   · SCREEN — pixels in the viewport element.
 * `toWorld` and `toScreen` are the only crossings. Nothing else should
 * touch `viewport.scale`.
 */

export function toWorld(viewport, screenX, screenY) {
  return {
    x: (screenX - viewport.x) / viewport.scale,
    y: (screenY - viewport.y) / viewport.scale,
  };
}

export function toScreen(viewport, worldX, worldY) {
  return {
    x: worldX * viewport.scale + viewport.x,
    y: worldY * viewport.scale + viewport.y,
  };
}

/**
 * Zoom about a fixed point.
 *
 * The point under the cursor must not move — that is the whole feel of
 * a wheel zoom, and getting it wrong makes a canvas feel like it is
 * fighting you. Solved by keeping the world point under the cursor
 * invariant rather than by scaling the offset, which is only equivalent
 * at the origin.
 */
export function zoomAbout(viewport, screenX, screenY, scale) {
  const next = clampScale(scale);
  const before = toWorld(viewport, screenX, screenY);
  return {
    scale: next,
    x: screenX - before.x * next,
    y: screenY - before.y * next,
  };
}

export const MIN_SCALE = 0.02;
export const MAX_SCALE = 16;
export const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/* ------------------------------------------------------------ rects -- */

export function rectOf(shape) {
  return { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
}

export const right = (r) => r.x + r.w;
export const bottom = (r) => r.y + r.h;
export const centerX = (r) => r.x + r.w / 2;
export const centerY = (r) => r.y + r.h / 2;

export function center(r) {
  return { x: centerX(r), y: centerY(r) };
}

/** The smallest rect containing all of them, or null for none. */
export function boundsOf(rects) {
  let out = null;
  for (const r of rects) {
    if (!r) continue;
    if (!out) { out = { x: r.x, y: r.y, w: r.w, h: r.h }; continue; }
    const x = Math.min(out.x, r.x);
    const y = Math.min(out.y, r.y);
    out = { x, y, w: Math.max(right(out), right(r)) - x, h: Math.max(bottom(out), bottom(r)) - y };
  }
  return out;
}

/** Do two rects overlap at all? Touching edges do not count. */
export function rectsIntersect(a, b) {
  return a.x < right(b) && right(a) > b.x && a.y < bottom(b) && bottom(a) > b.y;
}

/**
 * The rect between two dragged corners.
 *
 * Normalised, because a marquee dragged up and to the left is the same
 * rect as one dragged down and to the right — and a width of -80 selects
 * nothing at all, which is the classic "selection only works one way"
 * bug.
 */
export function rectFromPoints(a, b) {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

/* --------------------------------------------------------- rotation -- */

export function rotatePoint(px, py, ox, oy, degrees) {
  if (!degrees) return { x: px, y: py };
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = px - ox;
  const dy = py - oy;
  return { x: ox + dx * cos - dy * sin, y: oy + dx * sin + dy * cos };
}

/**
 * A rotated shape's four corners, in world space.
 *
 * Needed by hit testing and by the bounds of a multi-selection: the
 * axis-aligned rect of a rotated shape is not the shape's own rect, and
 * using the latter puts the selection outline visibly askew.
 */
export function cornersOf(shape) {
  const r = rectOf(shape);
  const cx = centerX(r);
  const cy = centerY(r);
  const points = [
    { x: r.x, y: r.y }, { x: right(r), y: r.y },
    { x: right(r), y: bottom(r) }, { x: r.x, y: bottom(r) },
  ];
  if (!shape.rotation) return points;
  return points.map((p) => rotatePoint(p.x, p.y, cx, cy, shape.rotation));
}

/** The axis-aligned box a rotated shape actually occupies. */
export function outerBounds(shape) {
  const pts = cornersOf(shape);
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

/* ------------------------------------------------------ hit testing -- */

/**
 * Is this world point on the shape?
 *
 * The point is rotated back into the shape's own frame rather than the
 * shape being rotated forward, which turns every hit test into the same
 * axis-aligned comparison whatever the angle.
 *
 * `slop` widens the target by a few world units. Without it a 2px border
 * at 16% zoom is a third of a screen pixel and effectively unclickable —
 * which is exactly the zoom a whole section chain is read at.
 */
export function hitShape(shape, point, slop = 0) {
  const r = rectOf(shape);
  const local = shape.rotation
    ? rotatePoint(point.x, point.y, centerX(r), centerY(r), -shape.rotation)
    : point;
  return local.x >= r.x - slop && local.x <= right(r) + slop
    && local.y >= r.y - slop && local.y <= bottom(r) + slop;
}

/** Distance from a point to a segment — the hit test for connectors. */
export function distanceToSegment(point, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(point.x - a.x, point.y - a.y);
  let t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
}

/** Distance from a point to a polyline. */
export function distanceToPath(point, points) {
  if (!points || points.length === 0) return Infinity;
  if (points.length === 1) return Math.hypot(point.x - points[0].x, point.y - points[0].y);
  let best = Infinity;
  for (let i = 0; i < points.length - 1; i += 1) {
    best = Math.min(best, distanceToSegment(point, points[i], points[i + 1]));
  }
  return best;
}

/* ----------------------------------------------------------- handles -- */

// Eight around the edge, named by compass point so a resize can be read
// in the code that performs it.
export const HANDLES = Object.freeze(['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']);

/** Where each handle sits on a rect, before rotation. */
export function handlePoint(rect, handle) {
  const midX = centerX(rect);
  const midY = centerY(rect);
  switch (handle) {
    case 'nw': return { x: rect.x, y: rect.y };
    case 'n': return { x: midX, y: rect.y };
    case 'ne': return { x: right(rect), y: rect.y };
    case 'e': return { x: right(rect), y: midY };
    case 'se': return { x: right(rect), y: bottom(rect) };
    case 's': return { x: midX, y: bottom(rect) };
    case 'sw': return { x: rect.x, y: bottom(rect) };
    case 'w': return { x: rect.x, y: midY };
    default: return { x: midX, y: midY };
  }
}

/**
 * A resize, as a new rect.
 *
 * The opposite edge is what stays put — dragging the east handle must
 * not move the west one — so each handle writes only the edges it owns
 * and the rest are left alone.
 *
 * `keepAspect` scales from the anchored corner by the larger of the two
 * ratios, which is what a shift-drag does everywhere and what stops the
 * shape jittering between the two axes as the pointer moves diagonally.
 *
 * A drag PAST the opposite edge flips the rect rather than producing a
 * negative width. Every renderer would draw that wrong, and a user who
 * drags too far expects a mirrored shape, not a vanished one.
 */
export function resizeRect(rect, handle, dx, dy, { keepAspect = false, minSize = 1 } = {}) {
  let { x, y, w, h } = rect;
  const holdsWest = handle.includes('w');
  const holdsEast = handle.includes('e');
  const holdsNorth = handle.startsWith('n');
  const holdsSouth = handle.startsWith('s');

  if (holdsWest) { x += dx; w -= dx; }
  if (holdsEast) { w += dx; }
  if (holdsNorth) { y += dy; h -= dy; }
  if (holdsSouth) { h += dy; }

  if (keepAspect && rect.w > 0 && rect.h > 0 && holdsWest !== holdsEast && holdsNorth !== holdsSouth) {
    const ratio = Math.max(Math.abs(w) / rect.w, Math.abs(h) / rect.h);
    const nextW = Math.sign(w || 1) * rect.w * ratio;
    const nextH = Math.sign(h || 1) * rect.h * ratio;
    if (holdsWest) x = right(rect) - nextW;
    if (holdsNorth) y = bottom(rect) - nextH;
    w = nextW;
    h = nextH;
  }

  if (w < 0) { x += w; w = -w; }
  if (h < 0) { y += h; h = -h; }
  return { x, y, w: Math.max(minSize, w), h: Math.max(minSize, h) };
}

/** The angle from a shape's centre to a point, in degrees, 0 = up. */
export function angleTo(cx, cy, px, py) {
  return (Math.atan2(py - cy, px - cx) * 180) / Math.PI + 90;
}

/** Rotation snapped to 15° while shift is held, and normalised to 0–359. */
export function normalizeAngle(degrees, snap = 0) {
  const value = snap ? Math.round(degrees / snap) * snap : degrees;
  return ((value % 360) + 360) % 360;
}
